import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, TrendingUp, Users, RefreshCw, Heart } from "lucide-react";
import { AIContentRenderer, AIChatBubble } from "@/components/AIContentRenderer";
import { BEYOND_AI_LOGO, BEYOND_AI, BeyondAIWordmark } from "@/lib/beyondai";

export default function AIAssistantPage() {
  const [activeTab, setActiveTab] = useState("receptionist");
  const [salonName, setSalonName] = useState('');
  useEffect(() => {
    axios.get(`${API}/settings?lite=true`).then(r => setSalonName(r.data?.salon_name || 'the salon')).catch(() => setSalonName('the salon'));
  }, []);
  // Receptionist chat
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: "Hi! I'm your virtual receptionist, powered by Beyond AI. I can help your customers choose the right service, answer questions about treatments, pricing, and aftercare. Try asking me something a customer might ask!" }
  ]);
  const [chatInput, setChatInput] = useState("");
  const [chatSessionId, setChatSessionId] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  // Daily summary
  const [dailySummary, setDailySummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  // Retention insights
  const [retentionInsights, setRetentionInsights] = useState(null);
  const [loadingRetention, setLoadingRetention] = useState(false);
  
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const userMsg = chatInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatInput("");
    setChatLoading(true);
    try {
      const res = await axios.post(`${API}/ai/booking-assistant`, {
        message: userMsg,
        session_id: chatSessionId
      });
      setChatSessionId(res.data.session_id);
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I'm having trouble right now. Please try again!" }]);
    }
    setChatLoading(false);
    inputRef.current?.focus();
  };

  const generateSummary = async () => {
    setLoadingSummary(true);
    try {
      const res = await axios.get(`${API}/ai/daily-summary`);
      setDailySummary(res.data.summary);
    } catch (e) {  }
    setLoadingSummary(false);
  };

  const generateRetention = async () => {
    setLoadingRetention(true);
    try {
      const res = await axios.get(`${API}/ai/retention-insights`);
      setRetentionInsights(res.data.insights);
    } catch (e) {  }
    setLoadingRetention(false);
  };

  const quickQuestions = [
    "What's the difference between BIAB and gel manicure?",
    "My nails keep breaking, what do you recommend?",
    "How long does a pedicure take?",
    "Do I need removal before a new set?",
    "What's the price for a full set with nail art?"
  ];

  const renderMarkdown = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('### ')) return <h3 key={i} className="font-semibold mt-3 mb-1">{line.replace(/^###\s*/, '')}</h3>;
      if (line.startsWith('## ')) return <h3 key={i} className="font-semibold mt-3 mb-1">{line.replace(/^##\s*/, '')}</h3>;
      if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="font-semibold mt-2">{line.replace(/\*\*/g, '')}</p>;
      if (line.startsWith('- ')) return <li key={i} className="ml-4">{line.replace(/^-\s*/, '').replace(/\*\*/g, '')}</li>;
      if (line.startsWith('* ')) return <li key={i} className="ml-4">{line.replace(/^\*\s*/, '').replace(/\*\*/g, '')}</li>;
      if (line.match(/^\d+\.\s/)) return <li key={i} className="ml-4">{line.replace(/\*\*/g, '')}</li>;
      if (line.trim()) return <p key={i}>{line.replace(/\*\*/g, '')}</p>;
      return <br key={i} />;
    });
  };

  return (
    <div className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2.5" style={{ fontFamily: 'Playfair Display' }}>
            <img src={BEYOND_AI_LOGO} alt="Beyond AI" className="w-9 h-9 rounded-2xl object-cover" />
            Beyond <span style={{ color: BEYOND_AI.teal }}>AI</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Ask Beyond AI anything across your services, clients and operations</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="rounded-xl">
          <TabsTrigger value="receptionist" className="rounded-lg flex items-center gap-1.5">
            <Heart size={14} /> Virtual Receptionist
          </TabsTrigger>
          <TabsTrigger value="summary" className="rounded-lg flex items-center gap-1.5">
            <img src={BEYOND_AI_LOGO} alt="" className="w-3.5 h-3.5 rounded object-cover" /> Daily Summary
          </TabsTrigger>
          <TabsTrigger value="retention" className="rounded-lg flex items-center gap-1.5">
            <TrendingUp size={14} /> Retention Insights
          </TabsTrigger>
        </TabsList>

        {/* Virtual Receptionist / Booking Assistant */}
        <TabsContent value="receptionist" className="mt-4">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Chat panel - 2/3 */}
            <Card className="lg:col-span-2 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] overflow-hidden flex flex-col" style={{ height: '600px' }}>
              {/* Header */}
              <div className="p-4 border-b border-border" style={{ background: BEYOND_AI.bg }}>
                <div className="flex items-center gap-3">
                  <img src={BEYOND_AI_LOGO} alt="Beyond AI" className="w-10 h-10 rounded-2xl object-cover" />
                  <div>
                    <h3 className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>
                      Beyond <span style={{ color: BEYOND_AI.teal }}>AI</span> · virtual receptionist
                    </h3>
                    <p className="text-xs text-muted-foreground">Powered by Beyond AI · only recommends services from your catalog</p>
                  </div>
                </div>
              </div>
              
              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {msg.role === 'assistant' && (
                        <div className="w-7 h-7 rounded-xl overflow-hidden mr-2 shrink-0 mt-1">
                          <img src={BEYOND_AI_LOGO} alt="Beyond AI" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className={`max-w-[85%] p-3 leading-relaxed ${
                        msg.role === 'user' 
                          ? 'chat-bubble-user text-sm' 
                          : 'chat-bubble-assistant'
                      }`}>
                        {msg.role === 'assistant' ? <AIChatBubble content={msg.content} /> : msg.content}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="w-7 h-7 rounded-xl overflow-hidden mr-2 shrink-0">
                        <img src={BEYOND_AI_LOGO} alt="Beyond AI" className="w-full h-full object-cover" />
                      </div>
                      <div className="chat-bubble-assistant p-3">
                        <p className="text-[10px] text-muted-foreground mb-1">Beyond AI is thinking…</p>
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" />
                          <div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{animationDelay: '0.15s'}} />
                          <div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{animationDelay: '0.3s'}} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              </ScrollArea>

              {/* Input */}
              <div className="p-3 border-t border-border bg-card">
                <div className="flex gap-2">
                  <Input 
                    ref={inputRef}
                    value={chatInput} 
                    onChange={e => setChatInput(e.target.value)} 
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()}
                    placeholder="Ask something a customer would ask..." 
                    className="rounded-xl" 
                    data-testid="ai-receptionist-input"
                  />
                  <Button size="icon" onClick={sendChat} disabled={chatLoading} className="rounded-xl shrink-0">
                    <Send size={16} />
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
                  Beyond AI · only recommends services from your configured catalog · never invents prices
                </p>
              </div>
            </Card>

            {/* Quick questions panel - 1/3 */}
            <div className="space-y-4">
              <Card className="p-4 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]">
                <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Playfair Display' }}>Try asking...</h3>
                <div className="space-y-2">
                  {quickQuestions.map((q, i) => (
                    <button 
                      key={i}
                      onClick={() => { setChatInput(q); inputRef.current?.focus(); }}
                      className="w-full text-left p-2.5 text-xs bg-secondary hover:bg-primary/10 rounded-xl transition-colors duration-150"
                    >
                      "{q}"
                    </button>
                  ))}
                </div>
              </Card>
              <Card className="p-4 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]">
                <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>How this works</h3>
                <div className="space-y-2 text-xs text-muted-foreground">
                  <p>The Beyond AI receptionist knows your entire service catalog including prices, durations, and descriptions.</p>
                  <p>It will ask clarifying questions and recommend the best service for each customer.</p>
                  <p>This is the same Beyond AI that appears on your public booking page for customers.</p>
                </div>
              </Card>
              <Button 
                variant="outline" 
                className="w-full rounded-xl text-xs" 
                onClick={() => { setChatMessages([{ role: 'assistant', content: `Hi! I'm ${salonName === 'the salon' ? 'your' : `${salonName}'s`} virtual receptionist, powered by Beyond AI. How can I help you today?` }]); setChatSessionId(null); }}
              >
                <RefreshCw size={12} className="mr-1" /> Start new conversation
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Daily Summary */}
        <TabsContent value="summary" className="mt-4">
          <Card className="p-6 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]" data-testid="ai-owner-summary-card" style={{ borderColor: BEYOND_AI.border }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <img src={BEYOND_AI_LOGO} alt="Beyond AI" className="w-7 h-7 rounded-xl object-cover" />
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display' }}>
                  Beyond <span style={{ color: BEYOND_AI.teal }}>AI</span> · daily operations summary
                </h3>
              </div>
              <Button onClick={generateSummary} disabled={loadingSummary} className="rounded-xl text-white border-0" size="sm"
                style={{ background: loadingSummary ? undefined : BEYOND_AI.gradient }}>
                {loadingSummary ? 'Beyond AI is analysing…' : 'Generate with Beyond AI'}
              </Button>
            </div>
            {dailySummary ? (
              <div className="max-h-[600px] overflow-y-auto pr-2 fade-in">
                <AIContentRenderer content={dailySummary} />
              </div>
            ) : (
              <div className="py-12 text-center">
                <img src={BEYOND_AI_LOGO} alt="Beyond AI" className="w-12 h-12 rounded-2xl object-cover mx-auto mb-3 opacity-30" />
                <p className="text-muted-foreground">Click "Generate with Beyond AI" to get a calm, clear read of today's operations, upcoming bookings, and suggested actions.</p>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Retention Insights */}
        <TabsContent value="retention" className="mt-4">
          <Card className="p-6 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]" style={{ borderColor: BEYOND_AI.border }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <img src={BEYOND_AI_LOGO} alt="Beyond AI" className="w-7 h-7 rounded-xl object-cover" />
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display' }}>
                  Beyond <span style={{ color: BEYOND_AI.teal }}>AI</span> · retention &amp; growth insights
                </h3>
              </div>
              <Button onClick={generateRetention} disabled={loadingRetention} className="rounded-xl text-white border-0" size="sm"
                style={{ background: loadingRetention ? undefined : BEYOND_AI.gradient }}>
                {loadingRetention ? 'Beyond AI is thinking…' : 'Generate with Beyond AI'}
              </Button>
            </div>
            {retentionInsights ? (
              <div className="max-h-[600px] overflow-y-auto pr-2 fade-in">
                <AIContentRenderer content={retentionInsights} />
              </div>
            ) : (
              <div className="py-12 text-center">
                <img src={BEYOND_AI_LOGO} alt="Beyond AI" className="w-12 h-12 rounded-2xl object-cover mx-auto mb-3 opacity-30" />
                <p className="text-muted-foreground">Ask Beyond AI for retention insights — lapsed clients, VIP engagement ideas, and win-back strategies.</p>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
